import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import { PageHeader, PageLayout } from "../components/layout";
import { Button, PageFilterBar, ScopeSelect, SearchInput, Spinner, Toggle } from "../components/ui";
import { Loading } from "../components";
import useTradeFeed, { isSearchTimeout } from "../tradeFeed/useTradeFeed";
import useHiddenGroups from "../tradeFeed/useHiddenGroups";
import TradeFeedCard from "../tradeFeed/TradeFeedCard";
import collapseForwards from "../tradeFeed/collapseForwards";
import TradeFeedModal from "../tradeFeed/TradeFeedModal";
import { isMobileDevice } from "../tradeFeed/whatsappChatLink";
import { setWhatsappTarget, useWhatsappTarget } from "../tradeFeed/whatsappTarget";
import "../styles/trade-feed.scss";

/**
 * Trade Feed — every WhatsApp message our Cooby seats see, live and unprocessed.
 *
 * This is the layer BEFORE the N8N ETL: nothing here is mapped to a product,
 * which is exactly why it is useful (a price you can act on 40 seconds after it
 * was posted). The collection carries no store id, so it cannot be scoped per
 * merchant: every user who has it sees the same messages.
 *
 * Who has it: master admins, and merchants whose store has the "Trade Feed &
 * assistant" Feature Matrix row in enforce mode (Alex, 2026-10-27). The route
 * (layouts/Routes.jsx, canUseOpenedFeature) and the API (`feature.open:
 * trade_feed`) apply the same rule, so a direct URL is as blocked as the
 * hidden nav row. Merchants' assistant has no Cooby tools.
 */

// Matches TradeFeedService::PERIODS — a value not in that list is rejected by
// validation, so the two lists must stay identical.
const PERIODS = [
  { key: "1h", label: "Last hour" },
  { key: "6h", label: "Last 6 hours" },
  { key: "1d", label: "Last 24 hours" },
  // The window multi-word searches are ranked over, so it is a choice too.
  { key: "3d", label: "Last 3 days" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
];

// The country picker is multi-select, so there is no "All countries" ROW: an
// option with a null id would land in the selection as a null code. Empty
// selection already means every country, and the placeholder says so.
const NO_COUNTRY_FILTER = "All countries";

// Cards the page fills to on its own before it waits for a scroll (Alex,
// 2026-10-16: "at least 100 matching cards present instantly").
const MIN_CARDS = 100;

// "Also search": the fields a search can look in besides the message text.
const SEARCH_SCOPE_OPTIONS = [
  { key: "group", label: "Group name" },
  { key: "name", label: "User name" },
  { key: "phone", label: "Mobile number" },
];

// How many cards before the end the next batch starts loading.
const PREFETCH_AHEAD = 50;

/** "30d" -> "last 30 days", from the same PERIODS list the server validates. */
const windowLabelFor = (key) => {
  const match = PERIODS.find((p) => p.key === key);
  return match ? match.label.toLowerCase() : "recent messages";
};

/** "Updated 12s ago" — the feed's own heartbeat, so a stalled poll is visible. */
const Heartbeat = ({ updatedAt, isLive }) => {
  const [, tick] = useState(0);

  useEffect(() => {
    if (!isLive) return undefined;
    const t = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [isLive]);

  // Nothing at all while a search is ranked rather than live: the old "Ranked
  // by relevance" line explained an internal detail nobody acts on.
  if (!isLive) return null;
  if (!updatedAt) return <span className="trade-feed__status">Connecting…</span>;

  const seconds = Math.max(0, Math.round((Date.now() - updatedAt) / 1000));

  return (
    <span className="trade-feed__status">
      <span className="trade-feed__pulse" aria-hidden="true" />
      Live · updated {seconds}s ago
    </span>
  );
};

const TradeFeed = () => {
  // `text` is what the field shows on every keystroke; `query` is the debounced
  // value that actually reaches the API (SearchInput debounces onSearch).
  // ?q= opens the page already searching (Deals → "Open in Trade Feed").
  const [initialQuery] = useState(() => new URLSearchParams(window.location.search).get("q")?.trim() || "");
  const [text, setText] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);
  // A LIST now, because a Region selects many countries at once. The single
  // `country` filter the API originally took is still accepted server-side, so
  // an already-open tab keeps working while this deploys.
  const [selected, setSelected] = useState([]);
  // Also a list: "Europe and the UK" is one question, and asking it used to
  // mean picking one region and then adding the other region's countries by
  // hand.
  const [regionKeys, setRegionKeys] = useState([]);
  const [period, setPeriod] = useState(null);
  const [countries, setCountries] = useState([]);
  // Region -> its countries, straight from the API. Deliberately NOT a table in
  // this file: the query builds its region membership from
  // config('trade-feed.regions'), and a second copy here is how the dropdown
  // and the filter start disagreeing about what "Europe" means.
  const [regions, setRegions] = useState([]);
  // "Only messages that have BOTH a picture and text."
  const [mediaAndText, setMediaAndText] = useState(false);
  // "Hide bulk" — messages longer than the server's character threshold. The
  // threshold itself is deliberately NOT held here: the label no longer shows
  // it, and the server is the only thing that applies it, so mirroring the
  // number in the client would only create something that could disagree.
  const [hideBulk, setHideBulk] = useState(false);
  // Search looks in the message text only, unless these widen it.
  const [includeGroup, setIncludeGroup] = useState(false);
  const [includeName, setIncludeName] = useState(false);
  const [includePhone, setIncludePhone] = useState(false);
  // The "Also search" dropdown is a view over the three flags above, which
  // stay the hook's (and the API's) shape.
  const searchScope = useMemo(
    () => [includeGroup && "group", includeName && "name", includePhone && "phone"].filter(Boolean),
    [includeGroup, includeName, includePhone]
  );
  const onSearchScopeChange = useCallback((next) => {
    const keys = next ?? [];
    setIncludeGroup(keys.includes("group"));
    setIncludeName(keys.includes("name"));
    setIncludePhone(keys.includes("phone"));
  }, []);
  // "Search older messages". Held as the QUERY it was asked for, not a bare
  // boolean, so typing a new term drops back to the default window in the same
  // render — a boolean reset by an effect would first fire the new term as an
  // all-time scan, then cancel it.
  const [allTimeFor, setAllTimeFor] = useState(null);
  const allTime = Boolean(query) && allTimeFor === query;
  const [openItem, setOpenItem] = useState(null);
  // The WhatsApp button's computer target (whatsappTarget.js).
  const waTarget = useWhatsappTarget();
  const [isMobile] = useState(isMobileDevice);
  // Groups hidden for the whole STORE, saved server-side and applied there to
  // every query — so they hold with or without filters and search.
  const { groups, hidden: hiddenGroups, setHidden: setGroupsHidden, version: hiddenVersion } = useHiddenGroups();
  const {
    items,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadMore,
    updatedAt,
    isLive,
    driver,
    related,
    message,
    searchWindow,
    scannedTo,
    extended,
  } = useTradeFeed({
    query,
    countries: selected,
    period,
    mediaAndText,
    hideBulk,
    allTime,
    hiddenVersion,
    includeGroup,
    includeName,
    includePhone,
  });
  // The label of the window the server applied ("last 30 days"), remembered so
  // the all-time view can still name what "back" returns to.
  const lastWindowRef = useRef(null);
  if (searchWindow) lastWindowRef.current = searchWindow;
  const windowLabel = windowLabelFor(searchWindow || lastWindowRef.current);
  const searchTimedOut = isSearchTimeout(error);

  /*
   | One card per message, however many groups it was forwarded into.
   |
   | A dealer posts the same stock into every group they are in, so one offer
   | arrived as eight identical cards and buried everything else. Collapsed
   | here rather than server-side because this cluster cannot group a large
   | result set at query time without timing out — and because the feed's own
   | accumulated array is already the right thing to collapse over, which
   | catches copies that straddle a page boundary for free.
   |
   | Memoised on the array identity: useTradeFeed replaces `items` only when
   | the data actually changes, so this runs once per page load and once per
   | live-tail prepend, not on every render.
   */
  const visibleItems = useMemo(() => collapseForwards(items), [items]);
  const visibleRelated = useMemo(() => collapseForwards(related), [related]);

  // Derived from libphonenumber server-side rather than a table in here, so the
  // filter's calling codes and the flags on the cards come from one source.
  useEffect(() => {
    let cancelled = false;

    axios
      .get("/api/trade-feed/countries")
      .then((response) => {
        if (cancelled) return;
        const rows = response?.data?.items ?? [];
        setRegions(response?.data?.regions ?? []);
        setCountries(rows.map((c) => ({ id: c.code, name: `${c.name} (${c.dial})` })));
      })
      .catch(() => {
        // The list is a convenience; the feed works unfiltered without it.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const periodOptions = useMemo(() => PERIODS, []);

  // {key,label} for the picker; the API rows arrive as {code,name,dial}.
  const countryOptions = useMemo(
    () => countries.map((c) => ({ key: c.id, label: c.name })),
    [countries]
  );

  // Built from the API's rows so the labels and the membership are the
  // server's, not a second opinion held here.
  const regionOptions = useMemo(
    () => regions.map((r) => ({ key: r.key, label: r.label })),
    [regions]
  );

  /**
   * The selection is the UNION of every ticked region's countries.
   *
   * Recomputed from the ticked keys rather than added to and subtracted from,
   * so un-ticking Europe takes Europe's countries back out — a merge-on-add
   * would leave them behind and the trigger would name a region the filter no
   * longer had. De-duplicated because regions can overlap.
   */
  const onRegionChange = useCallback(
    (next) => {
      const keys = next ?? [];
      setRegionKeys(keys);
      setSelected([
        ...new Set(
          keys.flatMap((key) => regions.find((r) => r.key === key)?.countries ?? []),
        ),
      ]);
    },
    [regions]
  );

  /**
   * Any hand edit of the countries — ticking or unticking one in the panel —
   * drops the region labels. They stop being true the moment the
   * selection is not exactly what those regions contain, and a trigger reading
   * "Europe" over a set that also holds Hong Kong is the page lying about its
   * own filter.
   */
  const onCountriesChange = useCallback((next) => {
    setSelected(next ?? []);
    setRegionKeys([]);
  }, []);

  // Hidden groups first, together, then the rest — each part keeps the
  // server's order (most recently active first).
  const groupOptions = useMemo(
    () =>
      [...groups.filter((g) => g.hidden), ...groups.filter((g) => !g.hidden)].map((g) => ({
        key: g.name,
        label: g.name,
      })),
    [groups]
  );

  // The picker hands back the whole next selection; the API takes changes, so
  // two people editing different groups at once never undo each other.
  const onHiddenGroupsChange = useCallback(
    (next) => {
      const nextSet = new Set(next ?? []);
      setGroupsHidden([...nextSet].filter((name) => !hiddenGroups.includes(name)), true);
      setGroupsHidden(hiddenGroups.filter((name) => !nextSet.has(name)), false);
    },
    [hiddenGroups, setGroupsHidden]
  );

  // An IntersectionObserver sentinel rather than react-infinite-scroll-component:
  // this page renders inside the app shell's own scroll container, and the
  // observer does not need to be told which ancestor that is. The button below
  // it is the real control — keyboard users and any browser without the
  // observer get the same behaviour.
  const sentinelRef = useRef(null);
  const loadMoreRef = useRef(loadMore);
  useEffect(() => {
    loadMoreRef.current = loadMore;
  });

  // The grid, so the observer can watch a card PREFETCH_AHEAD from the end:
  // the next batch is fetched while that many cards are still unread below,
  // so it is ready by the time the reader gets there.
  const gridRef = useRef(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMoreRef.current?.();
      },
      { rootMargin: "600px" },
    );
    observer.observe(node);
    const cards = gridRef.current?.children;
    if (cards?.length > PREFETCH_AHEAD) observer.observe(cards[cards.length - PREFETCH_AHEAD]);

    return () => observer.disconnect();
    // isLoadingMore re-arms the observer after each page: it only reports a
    // CHANGE in intersection, so a sentinel still in view after a short page
    // (forwards collapse 24 rows into a few cards) would never fire again.
    // visibleItems.length moves the early-warning card as the list grows.
  }, [hasMore, isLoadingMore, visibleItems.length]);

  // Keep going until there is a screenful. A page can come back short — its
  // rows folded into fewer cards, or the server stopped on time partway
  // through a rare search and handed back a cursor — and the reader should
  // not have to scroll to make the search carry on.
  useEffect(() => {
    if (!isLoading && !isLoadingMore && hasMore && !error && visibleItems.length < MIN_CARDS) {
      loadMore();
    }
  }, [isLoading, isLoadingMore, hasMore, error, visibleItems.length, loadMore]);

  const scannedToLabel = scannedTo
    ? new Date(scannedTo * 1000).toLocaleDateString(undefined, { day: "numeric", month: "short" })
    : null;

  const reset = useCallback(() => {
    setText("");
    setQuery("");
    setSelected([]);
    setRegionKeys([]);
    setMediaAndText(false);
    setHideBulk(false);
    setIncludeGroup(false);
    setIncludeName(false);
    setIncludePhone(false);
    setPeriod(null);
    setAllTimeFor(null);
  }, []);

  const hasFilters = Boolean(
    query || selected.length || regionKeys.length || period || mediaAndText || hideBulk ||
      includeGroup || includeName || includePhone,
  );


  return (
    <PageLayout className="leads-dark trade-feed">
      {/* The description was three lines of preamble on a phone before the
          first message. The search field's own placeholder already lists what
          is searchable, so this says only what that field cannot: where the
          messages come from, and that they are raw. */}
      <PageHeader
        title="Trade Feed"
        description="Live, unprocessed WhatsApp messages from our seats."
      />

      <main id="pages" className="trade-feed__main">
        {/* Row 1 — the shared PageFilterBar geometry (Listings, Leads, Orders):
            the search pill on the left, every dropdown pinned right (Alex,
            2026-10-16). Below the `lg` breakpoint PageFilterBar stacks the
            pill over the dropdowns, and on a phone the dropdowns become one
            swipeable strip. */}
        <PageFilterBar
          className="trade-feed__filterbar"
          label="Search and filter the trade feed"
          search={(
            <SearchInput
              size="lg"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onSearch={setQuery}
              // Longer than the 300ms default: every search here is a scan, and
              // the server keeps running a superseded one even after the
              // browser cancels it. Fewer, later requests matter more on this
              // page than shaving 200ms off the first result.
              debounce={500}
              onClear={() => {
                setText("");
                setQuery("");
              }}
              loading={isLoading && Boolean(query)}
              aria-label="Search WhatsApp messages"
              placeholder="Search message text — e.g. 5711 blue"
            />
          )}
        >
          {/* Country leads: Region is the shortcut that FILLS it. `searchable`
              because ~200 calling codes with no filter field is not a control;
              the ticks in the panel are the selection. */}
          <ScopeSelect
            label="Country"
            placeholder={NO_COUNTRY_FILTER}
            searchable
            // A count, because naming 43 countries would be the whole row.
            summary={(p) => `${p.length} ${p.length === 1 ? "country" : "countries"}`}
            options={countryOptions}
            value={selected}
            onChange={onCountriesChange}
          />

          {/* Multi-select: "Europe and the UK" is one question. Options and
              wording come from the API, i.e. from the config the QUERY uses. */}
          <ScopeSelect
            label="Region"
            placeholder="All regions"
            options={regionOptions}
            value={regionKeys}
            onChange={onRegionChange}
          />

          <ScopeSelect
            label="Message time"
            placeholder="Any time"
            multiple={false}
            options={periodOptions}
            value={period}
            onChange={setPeriod}
          />

          {/* Ticked = hidden, for everyone in this store. Not part of Reset:
              it is a saved store setting, not a filter on this view. */}
          <ScopeSelect
            label="Hide groups"
            placeholder="Hide groups"
            searchable
            summary={(p) => `${p.length} ${p.length === 1 ? "group" : "groups"} hidden`}
            options={groupOptions}
            value={hiddenGroups}
            onChange={onHiddenGroupsChange}
          />

          {/* What the search looks in besides the message text — one
              multi-select instead of three switches. None ticked = message
              text only, the default: a reference search should not light up
              every group or dealer whose NAME contains the digits. */}
          <ScopeSelect
            label="Also search"
            placeholder="Also search"
            summary={(p) => (p.length === 1 ? `Also: ${p[0].label}` : `Also: ${p.length} fields`)}
            options={SEARCH_SCOPE_OPTIONS}
            value={searchScope}
            onChange={onSearchScopeChange}
          />
        </PageFilterBar>

        {/* Row 2 — the live heartbeat on the left; the two content toggles
            pinned right, where the Smart Chat button was, and Reset after them
            when a search or filter is on (Alex, 2026-10-21). */}
        <div className="trade-feed__toolbar2">
          <div className="trade-feed__meta">
            {/* Suppressed while a load error is unresolved: a green "Live ·
                updated 9s ago" beside "The feed could not be loaded" is the
                page contradicting itself. */}
            {error && !items.length && !searchTimedOut ? (
              <span className="trade-feed__status">Not connected</span>
            ) : (
              <Heartbeat updatedAt={updatedAt} isLive={isLive} />
            )}
          </div>

          <div className="trade-feed__toggles">
            {/* Both, not either: a caption on an image is where a dealer
                states the reference and the price together. Server-side. */}
            {/* "Image +" (Alex, 2026-10-21): an image with text. */}
            <Toggle size="lg" label="Image +" checked={mediaAndText} onChange={setMediaAndText} />

            {/* Bulk = longer than the server's threshold (TRADE_FEED_BULK_CHARS,
                500 by the measured distribution), applied server-side. */}
            <Toggle size="lg" label="Hide bulk" checked={hideBulk} onChange={setHideBulk} />

            {/* Where the cards' WhatsApp button opens a chat on a computer:
                the desktop app when on, WhatsApp Web when off. Each person's
                own choice, remembered in the browser. Not offered on a phone,
                which always uses the app. */}
            {!isMobile && (
              <Toggle
                size="lg"
                label="WhatsApp app"
                checked={waTarget === "app"}
                onChange={(on) => setWhatsappTarget(on ? "app" : "web")}
              />
            )}

            {hasFilters && (
              <Button type="button" variant="ghost" size="lg" onClick={reset}>
                Reset
              </Button>
            )}
          </div>
        </div>

        {/* A scanning search (a word, a phrase, or a term the index cannot
            answer) is bounded server-side to a recent window when no period is
            chosen — otherwise a rare term reads millions of messages and times
            out. Said in place, with the way out, so a bounded search never
            passes for an all-time one. */}
        {query && searchWindow && !allTime && driver !== "disabled" ? (
          <p className="trade-feed__window" role="status">
            {/* Older matches now follow on their own once the window is used
                up, so this says what order they come in rather than offering
                a button to go and get them. */}
            {extended
              ? `Matches from the ${windowLabel} first, then older messages as you scroll.`
              : `Showing matches from the ${windowLabel} — older ones load automatically after.`}
          </p>
        ) : null}
        {allTime ? (
          <p className="trade-feed__window" role="status">
            Searching all messages — this can take a while.{" "}
            <button type="button" className="trade-feed__window-action" onClick={() => setAllTimeFor(null)}>
              Back to the {windowLabel}
            </button>
          </p>
        ) : null}

        {error && !items.length ? (
          <div className="trade-feed__empty">
            <p>
              {searchTimedOut
                ? "That search took too long."
                : "The feed could not be loaded."}
            </p>
            <p className="trade-feed__empty-hint">
              {/* A time-out is not an outage: the cluster stopped a scan that
                  could not fill a page inside its budget, and a more specific
                  term or a shorter period is the actual remedy. */}
              {searchTimedOut
                ? error?.response?.data?.message ||
                  "Try a more specific term, or narrow the time filter."
                : error?.response?.status === 403
                  ? "This page is restricted to RC master admins."
                  : "Check the WhatsApp message store is reachable, then try again."}
            </p>
            {searchTimedOut && allTime ? (
              <Button type="button" variant="secondary" size="lg" onClick={() => setAllTimeFor(null)}>
                Back to the {windowLabel}
              </Button>
            ) : null}
          </div>
        ) : null}

        {isLoading && !items.length ? <Loading type="table" showText /> : null}

        {/* Nothing yet, but the server stopped on time rather than at the end:
            the search is still walking back and continues by itself. Saying
            "no matches" here was the 2026-10-14 bug. */}
        {!isLoading && !items.length && !error && hasMore ? (
          <div className="trade-feed__empty" role="status">
            <p className="trade-feed__searching">
              <Spinner size="sm" inline label="Searching" />
              Searching older messages{scannedToLabel ? ` — back to ${scannedToLabel}` : ""}…
            </p>
          </div>
        ) : null}

        {!isLoading && !items.length && !error && !hasMore ? (
          <div className="trade-feed__empty">
            {/* Three different empty states, and telling them apart matters.
                "disabled" is not an absence of messages — it is this
                environment declining to show production ones, and rendering
                the generic "No messages yet." there sends someone hunting a
                bug in a feed that is working exactly as configured. */}
            {driver === "disabled" ? (
              <p>
                {message ||
                  "WhatsApp intake is not available in this environment."}
              </p>
            ) : (
              <p>
                {query && searchWindow && !allTime
                  ? `No matches in the ${windowLabel}.`
                  : hasFilters
                    ? "No messages match those filters."
                    : "No messages yet."}
              </p>
            )}
            {hasFilters && driver !== "disabled" ? (
              <Button type="button" variant="secondary" size="lg" onClick={reset}>
                Clear filters
              </Button>
            ) : null}
          </div>
        ) : null}

        {items.length ? (
          <>
            <div className="trade-feed__grid" ref={gridRef}>
              {visibleItems.map((item) => (
                <TradeFeedCard key={item.id} item={item} onOpen={setOpenItem} query={query} />
              ))}
            </div>

            <div ref={sentinelRef} className="trade-feed__sentinel" aria-hidden="true" />

            {hasMore && isLoadingMore ? (
              // A status line, not a disabled button: the greyed-out button
              // text was unreadable (2026-10-16).
              <p className="trade-feed__searching" role="status">
                <Spinner size="sm" inline label="Loading" />
                {scannedToLabel ? `Searching older messages — back to ${scannedToLabel}…` : "Loading more…"}
              </p>
            ) : hasMore ? (
              <div className="trade-feed__more">
                <Button type="button" variant="secondary" size="lg" onClick={loadMore}>
                  Load more
                </Button>
              </div>
            ) : error ? (
              // "Load more" failed. Saying "End of results" here would claim
              // there was nothing further when there may well be.
              <p className="trade-feed__end">
                {searchTimedOut
                  ? "Loading more took too long — try a more specific search."
                  : "More results could not be loaded."}
              </p>
            ) : (
              <p className="trade-feed__end">End of results</p>
            )}

            {/* The second, weaker set from a multi-keyword search: recent
                messages mentioning some of the terms but not the one that
                anchored the query. Kept visually and semantically apart from
                the results above, because they are a different promise — every
                card above contains what was searched for, these only might.

                Never paged: the query behind them is the one that cannot use an
                index, so it is capped server-side at a size a person can read.
                A "load more" here would quietly turn a bounded extra into an
                unbounded scan. */}
            {related.length ? (
              <section className="trade-feed__related" aria-labelledby="tf-related">
                <h2 className="trade-feed__related-title" id="tf-related">
                  Other recent mentions
                  <span className="trade-feed__related-hint">
                    Not an exact match — these contain some of your words
                  </span>
                </h2>

                <div className="trade-feed__grid">
                  {visibleRelated.map((item) => (
                    <TradeFeedCard key={`related-${item.id}`} item={item} onOpen={setOpenItem} query={query} />
                  ))}
                </div>
              </section>
            ) : null}
          </>
        ) : null}
      </main>

      <TradeFeedModal item={openItem} onClose={() => setOpenItem(null)} query={query} />
    </PageLayout>
  );
};

export default TradeFeed;
